import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import AcademyEnrollmentDetails from './AcademyEnrollmentDetails';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow } from './AcademyShared';

const STATUSES = ['active', 'completed', 'withdrawn', 'suspended'];

export default function AcademyEnrollments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [students, setStudents] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/enrollments?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/students?per_page=500&enrollable=1`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setStudents(Array.isArray(j.data) ? j.data : [])).catch(() => setStudents([]));
    fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=500&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setCohorts(Array.isArray(j.data) ? j.data : [])).catch(() => setCohorts([]));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    let reason = null;
    if (newStatus === 'withdrawn') {
      reason = window.prompt('Withdrawal reason:');
      if (reason === null) return;
    }
    const res = await fetch(`${API}/companies/${companyId}/academy/enrollments/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, withdrawal_reason: reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Status change failed'); return; }
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this enrollment?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/enrollments/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📝 Enrollments</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Enrollment</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Student', 'Cohort', 'Enrolled', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No enrollments</td></tr>}
              {list.map(en => (
                <tr key={en.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{en.enrollment_code}</td>
                  <td style={tdStyle}>{en.student ? en.student.full_name : '—'}</td>
                  <td style={tdStyle}>{en.cohort?.name || '—'}</td>
                  <td style={tdStyle}>{en.enrollment_date}</td>
                  <td style={tdStyle}><Badge value={en.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewing(en)}>Details</button>
                    {en.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(en.id, 'completed')}>Complete</button>}
                    {en.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(en.id, 'withdrawn')}>Withdraw</button>}
                    {en.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(en.id, 'suspended')}>Suspend</button>}
                    {en.status === 'suspended' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(en.id, 'active')}>Reactivate</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(en.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EnrollmentModal companyId={companyId} students={students} cohorts={cohorts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {viewing && <AcademyEnrollmentDetails companyId={companyId} enrollment={viewing} onClose={() => setViewing(null)} onSaved={fetchList} />}
    </div>
  );
}

export function EnrollmentModal({ companyId, students, cohorts, initialStudent, onClose, onSaved }) {
  const [form, setForm] = useState({
    student_id: initialStudent?.id || '', cohort_id: '', enrollment_date: new Date().toISOString().slice(0, 10),
    tuition_fee: '', discount_amount: '', discount_percent: '', notes: '', installments: [],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.student_id || !form.cohort_id) { alert('Student and Cohort are required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/enrollments`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Enrollment</h3>
        <FormRow label="Student *">
          <select style={inputStyle} value={form.student_id} disabled={Boolean(initialStudent)} onChange={e => setForm({ ...form, student_id: e.target.value })}>
            <option value="">Select student</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Cohort *">
          <select style={inputStyle} value={form.cohort_id} onChange={e => { const cohort = cohorts.find(c => String(c.id) === e.target.value); setForm({ ...form, cohort_id: e.target.value, tuition_fee: cohort?.price ?? '' }); }}>
            <option value="">Select cohort</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Enrollment Date">
          <input type="date" style={inputStyle} value={form.enrollment_date} onChange={e => setForm({ ...form, enrollment_date: e.target.value })} />
        </FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
          <FormRow label="Total Amount">
            <input type="number" step="0.01" style={inputStyle} value={form.tuition_fee} onChange={e => setForm({ ...form, tuition_fee: e.target.value })} />
          </FormRow>
          <FormRow label="Discount Amount">
            <input type="number" min="0" step="0.01" style={inputStyle} value={form.discount_amount} onChange={e => { const amount = e.target.value; const total = Number(form.tuition_fee) || 0; setForm({ ...form, discount_amount: amount, discount_percent: total ? (Number(amount || 0) * 100 / total).toFixed(2) : '' }); }} />
          </FormRow>
          <FormRow label="Discount Percentage">
            <div style={{ position: 'relative' }}><input type="number" min="0" max="100" step="0.01" style={{ ...inputStyle, paddingRight: 30 }} value={form.discount_percent} onChange={e => { const percent = e.target.value; setForm({ ...form, discount_percent: percent, discount_amount: ((Number(form.tuition_fee) || 0) * Number(percent || 0) / 100).toFixed(2) }); }} /><span style={{ position: 'absolute', right: 10, top: 8, color: '#64748b', pointerEvents: 'none' }}>%</span></div>
          </FormRow>
          <FormRow label="Final Fee">
            <input type="number" min="0" step="0.01" style={inputStyle} value={Math.max(0, (Number(form.tuition_fee) || 0) - (Number(form.discount_amount) || 0)).toFixed(2)} onChange={e => { const finalFee = Number(e.target.value || 0); const total = Number(form.tuition_fee) || 0; const discount = Math.max(0, total - finalFee); setForm({ ...form, discount_amount: discount.toFixed(2), discount_percent: total ? (discount * 100 / total).toFixed(2) : '' }); }} />
          </FormRow>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}><strong>Payment schedule</strong><button type="button" style={btnSmall} onClick={() => setForm({ ...form, installments: [...form.installments, { amount_due: '', due_date: '' }] })}>+ Add payment</button></div>
        {form.installments.map((installment, index) => <div key={index} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, marginBottom:8 }}><input type="number" min="0" step="0.01" style={inputStyle} placeholder="Amount" value={installment.amount_due} onChange={e => setForm({ ...form, installments: form.installments.map((item, i) => i === index ? { ...item, amount_due:e.target.value } : item) })} /><input type="date" style={inputStyle} value={installment.due_date} onChange={e => setForm({ ...form, installments: form.installments.map((item, i) => i === index ? { ...item, due_date:e.target.value } : item) })} /><button type="button" style={{ ...btnSmall, color:'#dc2626' }} onClick={() => setForm({ ...form, installments: form.installments.filter((_, i) => i !== index) })}>Remove</button></div>)}
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
